import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Trash2, Upload, Image, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const AdminGallery = () => {
  const [gallery, setGallery] = useState([]);
  const [universes, setUniverses] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    universe_id: "",
    character_id: "",
    title: "",
    description: "",
    media_type: "image",
    url: "",
    source: "",
  });

  const fetchData = async () => {
    try {
      const [galRes, uniRes, charRes] = await Promise.all([
        axios.get(`${API}/gallery`),
        axios.get(`${API}/universes`),
        axios.get(`${API}/characters`),
      ]);
      setGallery(galRes.data);
      setUniverses(uniRes.data);
      setCharacters(charRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setFormData({ ...formData, url: `${backendUrl}${response.data.url}` });
      toast.success("File uploaded!");
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/gallery`, {
        ...formData,
        character_id: formData.character_id || null,
      });
      toast.success("Gallery item added");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`);
      toast.success("Item deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const resetForm = () => {
    setFormData({
      universe_id: "",
      character_id: "",
      title: "",
      description: "",
      media_type: "image",
      url: "",
      source: "",
    });
  };

  const getUniverseName = (id) => {
    const uni = universes.find(u => u.id === id);
    return uni ? uni.name : "Unknown";
  };

  const filteredCharacters = formData.universe_id 
    ? characters.filter(c => c.universe_id === formData.universe_id)
    : characters;

  return (
    <AdminLayout title="Manage Gallery">
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-400">{gallery.length} items</p>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-gallery-btn"
        >
          <Plus className="w-4 h-4" />
          Add Item
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {gallery.map((item) => (
            <div key={item.id} className="card-surface overflow-hidden group">
              <div className="relative aspect-square">
                {item.media_type === "image" ? (
                  <img
                    src={item.url}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                    <Video className="w-12 h-12 text-slate-600" />
                  </div>
                )}
                <div className="absolute top-2 right-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                    className="bg-black/50 hover:bg-red-500 hover:text-white"
                    data-testid={`delete-gallery-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                  <div className="flex items-center gap-2 mb-1">
                    {item.media_type === "image" ? (
                      <Image className="w-3 h-3 text-amber-500" />
                    ) : (
                      <Video className="w-3 h-3 text-sky-500" />
                    )}
                    <span className="text-xs text-slate-400">{getUniverseName(item.universe_id)}</span>
                  </div>
                  <p className="text-sm text-white font-medium truncate">{item.title}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0B0F19] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">Add Gallery Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="gallery-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Universe</Label>
                <Select
                  value={formData.universe_id}
                  onValueChange={(value) => setFormData({ ...formData, universe_id: value, character_id: "" })}
                >
                  <SelectTrigger className="input-dark mt-1" data-testid="gallery-universe">
                    <SelectValue placeholder="Select universe" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    {universes.map((uni) => (
                      <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Character (optional)</Label>
                <Select
                  value={formData.character_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, character_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="input-dark mt-1" data-testid="gallery-character">
                    <SelectValue placeholder="Select character" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    <SelectItem value="none">None</SelectItem>
                    {filteredCharacters.map((char) => (
                      <SelectItem key={char.id} value={char.id}>{char.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input-dark mt-1"
                  required
                  data-testid="gallery-title"
                />
              </div>
              <div>
                <Label className="text-slate-300">Media Type</Label>
                <Select
                  value={formData.media_type}
                  onValueChange={(value) => setFormData({ ...formData, media_type: value })}
                >
                  <SelectTrigger className="input-dark mt-1" data-testid="gallery-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Description (optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-dark mt-1"
                data-testid="gallery-description"
              />
            </div>

            <div>
              <Label className="text-slate-300">URL</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="input-dark flex-1"
                  placeholder="https://... or upload file"
                  required
                  data-testid="gallery-url"
                />
                <label className="btn-secondary px-4 py-2 cursor-pointer flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading..." : "Upload"}
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Source (optional)</Label>
              <Input
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="input-dark mt-1"
                placeholder="Credit or source URL"
                data-testid="gallery-source"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="gallery-submit">
                Add Item
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminGallery;
